import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Typography,
  List,
  Paper,
  ListItem,
  ListItemText,
  Chip,
  Divider,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Tabs,
  Tab,
  Avatar,
  ListItemAvatar,
} from '@mui/material';
import {
  QrCodeScanner as ScanIcon,
  UpdateOutlined as StatusIcon,
  LocalParking as ParkingIcon,
  NoteAdd as NoteIcon,
  TrendingUp as TrendingIcon,
} from '@mui/icons-material';
import { activityService, ActivityStatistics } from '../../services/api';
import type { AgentActivity } from '../../services/api';
import { format, isToday, isYesterday, isThisWeek } from 'date-fns';
import { fr } from 'date-fns/locale';
import { getStatusLabel, getStatusColor } from '../../utils/vehicleUtils';

const RecentActivity: React.FC = () => {
  const [activities, setActivities] = useState<AgentActivity[]>([]);
  const [statistics, setStatistics] = useState<ActivityStatistics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterTab, setFilterTab] = useState<string>('all');

  useEffect(() => {
    loadActivities();
    loadStatistics();
  }, [filterTab]);

  const loadActivities = async () => {
    try {
      setLoading(true);
      const params: any = {};
      if (filterTab !== 'all') {
        params.action_type = filterTab;
      }
      const response = await activityService.getActivities(params);
      setActivities(response.data.data || []);
      setError('');
    } catch (err: any) {
      console.error('Error loading activities:', err);
      setError('Erreur lors du chargement de l\'historique');
    } finally {
      setLoading(false);
    }
  };

  const loadStatistics = async () => {
    try {
      const response = await activityService.getStatistics();
      setStatistics(response.data);
    } catch (err) {
      console.error('Error loading statistics:', err);
    }
  };

  const getActivityIcon = (actionType: string) => {
    switch (actionType) {
      case 'scan':
        return <ScanIcon />;
      case 'status_update':
        return <StatusIcon />;
      case 'parking_assign':
      case 'parking_release':
        return <ParkingIcon />;
      case 'note_add':
        return <NoteIcon />;
      default:
        return <TrendingIcon />;
    }
  };

  const getActivityColor = (actionType: string) => {
    switch (actionType) {
      case 'scan':
        return 'primary';
      case 'status_update':
        return 'info';
      case 'parking_assign':
        return 'success';
      case 'parking_release':
        return 'warning';
      case 'note_add':
        return 'secondary';
      default:
        return 'default';
    }
  };

  const getActivityLabel = (actionType: string) => {
    switch (actionType) {
      case 'scan':
        return 'Scan';
      case 'status_update':
        return 'Mise à jour statut';
      case 'parking_assign':
        return 'Parking assigné';
      case 'parking_release':
        return 'Parking libéré';
      case 'note_add':
        return 'Note ajoutée';
      default:
        return actionType;
    }
  };

  const getActivityDescription = (activity: AgentActivity) => {
    const plate = activity.metadata?.vehicle_plate || activity.vehicle?.registration_number || 'Véhicule';
    
    switch (activity.action_type) {
      case 'scan':
        return `Scan de ${plate}`;
      case 'status_update':
        return `${plate}: ${getStatusLabel((activity.old_value || 'ready') as any)} → ${getStatusLabel((activity.new_value || 'ready') as any)}`;
      case 'parking_assign':
        return `${plate} assigné à la place ${activity.new_value}`;
      case 'parking_release':
        return `${plate} libéré de la place ${activity.old_value}`;
      case 'note_add':
        return `Note ajoutée pour ${plate}`;
      default:
        return `Action sur ${plate}`;
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    
    if (isToday(date)) {
      return `Aujourd'hui à ${format(date, 'HH:mm')}`;
    } else if (isYesterday(date)) {
      return `Hier à ${format(date, 'HH:mm')}`;
    } else if (isThisWeek(date)) {
      return format(date, 'EEEE à HH:mm', { locale: fr });
    } else {
      return format(date, 'dd MMM yyyy à HH:mm', { locale: fr });
    }
  };

  return (
    <Box 
      sx={{ 
        width: '100%',
        minHeight: '100%',
        pt: { xs: 2, sm: 3 },
        px: { xs: 2, sm: 3 },
        pb: { xs: 20, sm: 12 }, // Extra padding to avoid footer overlap on mobile
      }}
    >
      <Container maxWidth="md" disableGutters sx={{ height: '100%' }}>
      <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
        Activité Récente
      </Typography>

      {/* Statistics Cards */}
      {statistics && (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2, mb: 3 }}>
          <Card elevation={2}>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h4" color="primary" fontWeight={600}>
                {statistics.today_activities}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Aujourd'hui
              </Typography>
            </CardContent>
          </Card>
          <Card elevation={2}>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h4" color="secondary" fontWeight={600}>
                {statistics.this_week_activities}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Cette semaine
              </Typography>
            </CardContent>
          </Card>
          <Card elevation={2}>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h4" fontWeight={600}>
                {statistics.total_scans}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Total scans
              </Typography>
            </CardContent>
          </Card>
          <Card elevation={2}>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h4" fontWeight={600}>
                {statistics.total_notes}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Notes ajoutées
              </Typography>
            </CardContent>
          </Card>
        </Box>
      )}

      {/* Filter Tabs */}
      <Paper elevation={2} sx={{ mb: 3 }}>
        <Tabs
          value={filterTab}
          onChange={(_, newValue) => setFilterTab(newValue)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="Toutes" value="all" />
          <Tab label="Scans" value="scan" />
          <Tab label="Statuts" value="status_update" />
          <Tab label="Parking" value="parking_assign" />
          <Tab label="Notes" value="note_add" />
        </Tabs>
      </Paper>

      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Loading State */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : activities.length === 0 ? (
        /* Empty State */
        <Paper elevation={1} sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary" gutterBottom>
            📋 Aucune activité
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {filterTab === 'all' 
              ? 'Vos actions apparaîtront ici' 
              : `Aucune action de type "${getActivityLabel(filterTab)}"`}
          </Typography>
        </Paper>
      ) : (
        /* Activity List */
        <List sx={{ bgcolor: 'background.paper', borderRadius: 2 }}>
          {activities.map((activity, index) => (
            <React.Fragment key={activity.id}>
              <ListItem alignItems="flex-start">
                <ListItemAvatar>
                  <Avatar sx={{ bgcolor: `${getActivityColor(activity.action_type)}.main` }}>
                    {getActivityIcon(activity.action_type)}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <Typography variant="body1" fontWeight={500}>
                        {getActivityDescription(activity)}
                      </Typography>
                      <Chip 
                        label={getActivityLabel(activity.action_type)}
                        size="small"
                        color={getActivityColor(activity.action_type) as any}
                        sx={{ height: 20 }}
                      />
                    </Box>
                  }
                  secondary={
                    <Typography variant="caption" color="text.secondary">
                      {formatDate(activity.created_at)}
                    </Typography>
                  }
                />
              </ListItem>
              {index < activities.length - 1 && <Divider variant="inset" component="li" />}
            </React.Fragment>
          ))}
        </List>
      )}
      </Container>
    </Box>
  );
};

export default RecentActivity;
